import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { Button, ToggleGroup } from "@/components/ui";
import {
  DataTable,
  DataTablePrimaryText,
  type DataTableColumn,
} from "@/components/table";
import type { SettlementLineRow } from "@/services/adminSettlementService";
import { economicEventTypeLabel } from "@/lib/economicEventLabels";
import {
  type SettlementFlowFilter,
  type SettlementInvoiceFilter,
  type SettlementPaymentFilter,
  settlementFlowFilterOptions,
  settlementInvoiceAmountCents,
  settlementInvoiceFilterOptions,
  settlementPaymentAmount,
  settlementPaymentFilterOptions,
} from "../settlementUi";
import { formatDateTime } from "@/lib/datetime";
import { buildListResetToken } from "@/components/common";
import { SettlementLineAmountCell } from "./SettlementLineAmountCell";
import {
  SettlementInvoiceStatusCell,
  SettlementPaymentStatusCell,
} from "./SettlementLineStatusCell";

type SettlementLineSortColumn = "event" | "invoiceAmount" | "paymentAmount";

export function SettlementLinesSection({
  lines,
  totalLineCount,
  selected,
  flowFilter,
  paymentFilter,
  invoiceFilter,
  hasSettlementDocumentsRead,
  busy,
  onToggleLine,
  onSelectionChange,
  onFlowFilterChange,
  onPaymentFilterChange,
  onInvoiceFilterChange,
  onSelectAll,
  onDeselectAll,
  onViewFePdf,
  onDownloadFePdf,
  onViewSupplierInvoice,
  onDownloadSupplierInvoice,
}: {
  lines: SettlementLineRow[];
  totalLineCount: number;
  selected: Set<string>;
  flowFilter: SettlementFlowFilter;
  paymentFilter: SettlementPaymentFilter;
  invoiceFilter: SettlementInvoiceFilter;
  hasSettlementDocumentsRead: boolean;
  busy: boolean;
  onToggleLine: (lineId: string) => void;
  onSelectionChange: (next: Set<string>) => void;
  onFlowFilterChange: (filter: SettlementFlowFilter) => void;
  onPaymentFilterChange: (filter: SettlementPaymentFilter) => void;
  onInvoiceFilterChange: (filter: SettlementInvoiceFilter) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onViewFePdf: (documentId: string) => void;
  onDownloadFePdf: (documentId: string) => void;
  onViewSupplierInvoice: (documentId: string) => void;
  onDownloadSupplierInvoice: (documentId: string) => void;
}) {
  const { t } = useTranslation("payments");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);

  const flowOptions = settlementFlowFilterOptions(t);
  const paymentOptions =
    flowFilter === "all" ? [] : settlementPaymentFilterOptions(flowFilter, t);
  const invoiceOptions =
    flowFilter === "all" ? [] : settlementInvoiceFilterOptions(flowFilter, t);

  const filtersActive =
    flowFilter !== "all" || paymentFilter !== "all" || invoiceFilter !== "all";
  const emptyMessage =
    totalLineCount === 0
      ? t("settlements.detail.empty")
      : t("settlements.detail.emptyFiltered");

  const columns = useMemo((): DataTableColumn<
    SettlementLineRow,
    SettlementLineSortColumn
  >[] => {
    return [
      {
        id: "event",
        header: t("settlements.detail.colEvent"),
        sortKey: "event",
        sortKind: "date",
        getSortValue: (line) =>
          line.event_created_at ? Date.parse(line.event_created_at) : null,
        defaultWidth: 170,
        cell: (line) => {
          const eventLabel = economicEventTypeLabel(line.event_type, t);
          return (
            <div>
              <DataTablePrimaryText>{eventLabel}</DataTablePrimaryText>
              <div className="text-surface-500 text-xs">
                {line.event_created_at
                  ? formatDateTime(line.event_created_at)
                  : t("shared.emptyCell")}
              </div>
            </div>
          );
        },
      },
      {
        id: "invoiceAmount",
        header: t("settlements.detail.colInvoiceAmount"),
        sortKey: "invoiceAmount",
        sortKind: "number",
        getSortValue: (line) => settlementInvoiceAmountCents(line),
        defaultWidth: 110,
        cell: (line) => (
          <SettlementLineAmountCell
            cents={settlementInvoiceAmountCents(line)}
            currency={line.currency}
          />
        ),
      },
      {
        id: "invoiceStatus",
        header: t("settlements.detail.colInvoiceStatus"),
        defaultWidth: 160,
        cell: (line) => (
          <SettlementInvoiceStatusCell
            line={line}
            hasDocumentsRead={hasSettlementDocumentsRead}
            busy={busy}
            onViewFePdf={onViewFePdf}
            onDownloadFePdf={onDownloadFePdf}
            onViewSupplierInvoice={onViewSupplierInvoice}
            onDownloadSupplierInvoice={onDownloadSupplierInvoice}
          />
        ),
      },
      {
        id: "paymentAmount",
        header: t("settlements.detail.colPaymentAmount"),
        sortKey: "paymentAmount",
        sortKind: "number",
        getSortValue: (line) => settlementPaymentAmount(line)?.cents ?? null,
        defaultWidth: 110,
        cell: (line) => {
          const paymentAmount = settlementPaymentAmount(line);
          return (
            <SettlementLineAmountCell
              cents={paymentAmount?.cents ?? null}
              currency={line.currency}
              direction={paymentAmount?.direction}
            />
          );
        },
      },
      {
        id: "paymentStatus",
        header: t("settlements.detail.colPaymentStatus"),
        defaultWidth: 160,
        cell: (line) => <SettlementPaymentStatusCell line={line} />,
      },
    ];
  }, [
    busy,
    hasSettlementDocumentsRead,
    onViewFePdf,
    onDownloadFePdf,
    onViewSupplierInvoice,
    onDownloadSupplierInvoice,
    t,
  ]);

  const segmentedClass =
    "h-8 [&>button]:px-2.5 [&>button]:text-[13px] [&>button]:whitespace-nowrap";

  const filterGroup = (label: string, control: ReactNode) => (
    <div className="flex min-w-0 items-center gap-2">
      <span className="text-surface-500 shrink-0 text-xs font-medium">
        {label}
      </span>
      <div className="-mx-1 min-w-0 overflow-x-auto px-1 pb-0.5">{control}</div>
    </div>
  );

  const toolbar = (
    <div className="flex w-full flex-col gap-2.5">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {filterGroup(
          t("settlements.detail.flowFilter"),
          <ToggleGroup
            variant="segmented-neutral"
            value={flowFilter}
            onChange={onFlowFilterChange}
            options={flowOptions}
            ariaLabel={t("settlements.detail.flowFilter")}
            className={segmentedClass}
          />,
        )}
        <div className="flex shrink-0 items-center gap-1.5">
          {filtersActive && (
            <span className="text-surface-500 mr-1 text-xs tabular-nums">
              {t("settlements.detail.filteredCount", {
                shown: lines.length,
                total: totalLineCount,
              })}
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onSelectAll}
          >
            {t("settlements.detail.selectAll")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDeselectAll}
            disabled={selected.size === 0}
          >
            {t("settlements.detail.deselectAll")}
          </Button>
        </div>
      </div>
      {flowFilter !== "all" && (
        <div className="border-surface-200/70 flex flex-wrap items-center gap-x-5 gap-y-2 border-t pt-2.5">
          {filterGroup(
            t("settlements.detail.paymentFilter"),
            <ToggleGroup
              variant="segmented-neutral"
              value={paymentFilter}
              onChange={onPaymentFilterChange}
              options={paymentOptions}
              ariaLabel={t("settlements.detail.paymentFilter")}
              className={segmentedClass}
            />,
          )}
          {filterGroup(
            t("settlements.detail.invoiceFilter"),
            <ToggleGroup
              variant="segmented-neutral"
              value={invoiceFilter}
              onChange={onInvoiceFilterChange}
              options={invoiceOptions}
              ariaLabel={t("settlements.detail.invoiceFilter")}
              className={segmentedClass}
            />,
          )}
        </div>
      )}
    </div>
  );

  return (
    <section className="min-w-0 space-y-3">
      <div className="px-0.5">
        <h2 className="text-surface-900 text-[15px] font-semibold tracking-tight">
          {t("settlements.detail.linesTitle")}
        </h2>
        <p className="text-surface-500 mt-0.5 text-[13px]">
          {t("settlements.detail.linesSubtitle")}
        </p>
      </div>

      <DataTable<SettlementLineRow, SettlementLineSortColumn>
        labels={tableLabels}
        closeLabel={tCommon("close")}
        loadingLabel={tCommon("loading")}
        rows={lines}
        columns={columns}
        getRowKey={(row) => row.id}
        defaultSortColumn="event"
        defaultSortDirection="desc"
        preferencesKey="settlement-lines"
        resetToken={buildListResetToken([
          flowFilter,
          paymentFilter,
          invoiceFilter,
        ])}
        emptyMessage={emptyMessage}
        selectedIds={selected}
        onSelectedIdsChange={onSelectionChange}
        hideSelectionToolbar
        onRowClick={(row) => onToggleLine(row.id)}
        openRowLabel={(row) =>
          t("settlements.detail.selectLine", {
            activity: row.activity_name ?? row.id,
          })
        }
        rowClassName={(row) =>
          selected.has(row.id)
            ? "bg-primary-50/50 dark:bg-primary-50/30"
            : undefined
        }
        toolbar={toolbar}
      />
    </section>
  );
}
